import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/safeSupabase';
import { recoverSession, noteAuthEvent, serverNow, getClockSkewMs, describeClockSkew, setSessionExpiredHandler } from '@/lib/authResilience';
import { markIntentionalSignOut, logClientError } from '@/lib/errorLogger';
import type { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { ImpersonationContext } from '@/contexts/ImpersonationContext';

export type AppRole = 'super_admin' | 'platform_admin' | 'org_owner' | 'org_admin' | 'agent';

interface Organization {
  id: string;
  name: string;
  slug: string;
  owner_id: string;
  subscription_status: string;
  subscription_plan: string | null;
  max_seats: number;
  member_count: number;
  trial_ends_at: string | null;
  /**
   * org_chain_has_valid_subscription(): the database's own answer — this org
   * or any org above it (never the platform root) is active, exempt, or in
   * trial. RLS lets the member in on exactly this, so the lock screen must
   * too: an agency made under an exempt IMO starts 'trialing' with its own
   * clock, and checking only that clock locked its people out of an app the
   * database was still serving them. Undefined when the read failed.
   */
  chain_subscription_valid?: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: { full_name: string; email: string; org_id: string | null; status: string; has_client_book?: boolean } | null;
  roles: AppRole[];
  org: Organization | null;
  loading: boolean;
  slowLoad: boolean;

  // Computed role checks
  isSuperAdmin: boolean;
  isPlatformAdmin: boolean;
  /** True if the viewer can open /super-admin (super_admin OR platform_admin). */
  canAccessAdminPanel: boolean;
  isOrgOwner: boolean;
  isOrgAdmin: boolean;
  isAgent: boolean;
  canManageTeam: boolean;
  canManageBilling: boolean;
  canViewAudit: boolean;
  isSoloOrg: boolean;
  isTrialExpired: boolean;
  /** True when the current org's owner holds super_admin or platform_admin —
   * the inner-circle "Account" sidebar lane is exclusive to these orgs. */
  isPlatformStaffOrg: boolean;

  /** @deprecated Use isSuperAdmin or canManageTeam instead */
  isAdmin: boolean;

  refreshUserData: () => Promise<void>;
  /** Signs out this device only; the person's other devices stay signed in. */
  signOut: () => Promise<void>;
  /** Signs out every device on the account, this one included (Settings → Security). */
  signOutEverywhere: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<{ full_name: string; email: string; org_id: string | null; status: string } | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [org, setOrg] = useState<Organization | null>(null);
  const [isPlatformStaffOrg, setIsPlatformStaffOrg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [slowLoad, setSlowLoad] = useState(false);
  const fetchInFlight = useRef(false);
  // Distinguishes a user-clicked Sign Out from an involuntary session drop
  // (clock skew, mangled refresh, network blip) so only the former logs out instantly.
  const userSignOut = useRef(false);
  const recovering = useRef(false);

  const fetchUserData = async (userId: string) => {
    if (fetchInFlight.current) return;
    fetchInFlight.current = true;
    try {
      const [profileRes, rolesRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', userId).single(),
        supabase.from('user_roles').select('role, org_id').eq('user_id', userId),
      ]);

      // If either query returned no data (RLS failure, network blip, mid-refresh
      // 401), keep stale state. Supabase returns [] for "no roles", never null, so
      // null roles means the query failed — treating it as "no roles" wiped an
      // org owner's role on a transient error and bounced them off org views.
      if (!profileRes.data || !rolesRes.data) return;

      const profileData = {
        full_name: profileRes.data.full_name ?? '',
        email: profileRes.data.email ?? '',
        org_id: profileRes.data.org_id ?? null,
        status: profileRes.data.status ?? 'active',
        // Undefined before 20260917000002 is applied; hidesPersonalClients()
        // treats anything but an explicit false as "carries a book".
        has_client_book: (profileRes.data as { has_client_book?: boolean }).has_client_book,
      };
      setProfile(profileData);

      // Scope role booleans to the current org: keep rows for the user's current
      // org plus org_id=NULL rows (super_admin). This prevents orphan org_owner
      // rows for discarded workspaces from leaking privileges into the UI.
      const allRoleRows = (rolesRes.data ?? []) as { role: AppRole; org_id: string | null }[];
      const activeRoles = allRoleRows
        .filter(r => r.org_id === null || r.org_id === profileData?.org_id)
        .map(r => r.role);
      setRoles(activeRoles);

      // Fetch org if user has one
      if (profileData?.org_id) {
        const [{ data: orgData }, { count: memberCount }, chainRes] = await Promise.all([
          supabase
            .from('organizations')
            .select('id, name, slug, owner_id, subscription_status, subscription_plan, max_seats, trial_ends_at')
            .eq('id', profileData.org_id)
            .single(),
          supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true })
            .eq('org_id', profileData.org_id),
          supabase.rpc('org_chain_has_valid_subscription', { p_org: profileData.org_id }),
        ]);
        if (chainRes.error) {
          // Fail soft: the lock screen falls back to the org's own status.
          void logClientError({
            error_message: `rpc: org_chain_has_valid_subscription: ${chainRes.error.message}`,
            query_name: 'org_chain_has_valid_subscription',
            error_type: 'rpc',
          });
        }
        const chainValid = !chainRes.error && typeof chainRes.data === 'boolean' ? chainRes.data : undefined;
        setOrg(orgData ? {
          ...(orgData as Omit<Organization, 'member_count' | 'chain_subscription_valid'>),
          member_count: memberCount ?? 1,
          chain_subscription_valid: chainValid,
        } : null);

        // Is this org owned by platform staff (super_admin/platform_admin)? Gates the
        // inner-circle "Account" sidebar lane. `is_platform_staff_org` isn't in the
        // auto-generated Supabase types yet (that file is off-limits per CLAUDE.md) —
        // cast through unknown to satisfy the typed rpc signature, matching the pattern
        // in scenarioStore.ts's duplicateClient.
        if (orgData) {
          const { data: isStaffOrg, error: staffOrgErr } = await (supabase.rpc as unknown as (
            fn: 'is_platform_staff_org',
            args: { p_org: string },
          ) => Promise<{ data: boolean | null; error: Error | null }>)('is_platform_staff_org', {
            p_org: orgData.id,
          });
          if (staffOrgErr) {
            // A transport/RPC failure must never strip the inner circle of its
            // Account lane (2026-09-04: a silent fail-closed here hid All
            // Clients from Andrew and Dylan). Log it, then fall back to what we
            // already know: a viewer who IS platform staff lives in a
            // platform-staff org by definition.
            void logClientError({
              error_message: `rpc: is_platform_staff_org: ${staffOrgErr.message}`,
              query_name: 'is_platform_staff_org',
              error_type: 'rpc',
            });
            setIsPlatformStaffOrg(activeRoles.includes('super_admin') || activeRoles.includes('platform_admin'));
          } else {
            setIsPlatformStaffOrg(!!isStaffOrg);
          }
        } else {
          setIsPlatformStaffOrg(false);
        }
      } else {
        setOrg(null);
        setIsPlatformStaffOrg(false);
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    } finally {
      fetchInFlight.current = false;
    }
  };

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          await fetchUserData(session.user.id);
        }
      } catch (err) {
        console.error('Auth init failed:', err);
      } finally {
        setLoading(false);
        setSlowLoad(false);
      }

      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        // Keep the server-clock estimate current from tokens minted just now
        // (noteAuthEvent decides which events carry one; a tab refocus does not).
        // We DON'T warn here: a wrong clock that's still working fine is not the
        // user's problem. The clock is only surfaced if it actually causes a
        // sign-out (below).
        noteAuthEvent(event, session);
        if (session?.user) {
          userSignOut.current = false;
          setSession(session);
          setUser(session.user);
          fetchUserData(session.user.id);
          return;
        }
        // Involuntary SIGNED_OUT (not a Sign Out click): attempt one server-validated
        // recovery before booting to login. Refresh tokens are checked against the
        // SERVER's clock, so this survives skewed laptop clocks and transient drops.
        // If recovery succeeds the SDK emits TOKEN_REFRESHED and the branch above
        // restores state; only a genuinely dead session falls through to logout.
        if (event === 'SIGNED_OUT' && !userSignOut.current && !recovering.current) {
          recovering.current = true;
          recoverSession().then((recovered) => {
            recovering.current = false;
            if (recovered) return;
            setSession(null);
            setUser(null);
            setProfile(null);
            setRoles([]);
            setOrg(null);
            setIsPlatformStaffOrg(false);
            // This is an ACTUAL involuntary sign-out. Only now do we surface the clock —
            // and only if it's off enough to plausibly be the cause (Andrew's situation).
            // Fixed id de-dupes against the same message from an in-flight operation.
            const off = describeClockSkew(getClockSkewMs());
            toast.error(
              off
                ? `You've been signed out. Your device clock is off by ${off}, which can cause this — please set your date & time to automatic.`
                : 'Your session has expired. Please sign in again.',
              { id: 'session-expired' },
            );
          });
          return;
        }
        setSession(null);
        setUser(null);
        setProfile(null);
        setRoles([]);
        setOrg(null);
        setIsPlatformStaffOrg(false);
      });
      subscription = data.subscription;
    };

    const slowTimer = setTimeout(() => setSlowLoad(true), 5_000);
    const safetyTimer = setTimeout(() => setLoading(false), 10_000);

    init().then(() => {
      clearTimeout(slowTimer);
      clearTimeout(safetyTimer);
    });

    return () => {
      clearTimeout(slowTimer);
      clearTimeout(safetyTimer);
      subscription?.unsubscribe();
    };
  }, []);

  // The auth-resilience layer calls this when a refresh is unrecoverable. Clean up
  // locally and route to login. No toast here — the originating operation surfaces
  // the "session expired" message via sanitizeError; marking the sign-out intentional
  // keeps the resulting SIGNED_OUT from re-entering the recovery path.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      userSignOut.current = true;
      supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      setUser(null);
      setSession(null);
      setProfile(null);
      setRoles([]);
      setOrg(null);
      setIsPlatformStaffOrg(false);
      queryClient.clear();
    });
    return () => setSessionExpiredHandler(null);
  }, [queryClient]);

  const refreshUserData = async () => {
    if (user) await fetchUserData(user.id);
  };

  // Sign Out ends this device only (Drew, 2026-11-04: signing out on a phone
  // must not throw the laptop out of a client meeting); Settings → Security's
  // Sign out everywhere ends them all. Both clear this tab the same way.
  const endSession = async (scope: 'local' | 'global') => {
    userSignOut.current = true;
    markIntentionalSignOut(); // so the error logger doesn't record this as an involuntary sign-out
    await supabase.auth.signOut({ scope });
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    setOrg(null);
    setIsPlatformStaffOrg(false);
    // STALE CACHE FIX — CRITICAL — DO NOT REMOVE (3rd recurrence of stale-cache bug)
    // Wipe the entire React Query cache so the next user session starts clean.
    // Without this, cached queries (scenarios, drafts, feature-flags, etc.) persist
    // across login sessions and the new user sees the previous user's data.
    // See CLAUDE.md rule #9: "Never break query cache key scoping."
    queryClient.clear();
    // Clear any cached session/token data to prevent stale rehydration on refresh
    localStorage.removeItem('supabase.auth.token');
    // Clear all supabase-related keys from localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-')) localStorage.removeItem(key);
    });
    sessionStorage.clear();
  };
  const signOut = () => endSession('local');
  const signOutEverywhere = () => endSession('global');

  const isSuperAdmin = roles.includes('super_admin');
  const isPlatformAdmin = roles.includes('platform_admin');
  const canAccessAdminPanel = isSuperAdmin || isPlatformAdmin;
  const isOrgOwner = roles.includes('org_owner');
  const isOrgAdmin = roles.includes('org_admin');
  const isAgent = roles.includes('agent');
  const canManageTeam = isSuperAdmin || isOrgOwner || isOrgAdmin;
  const canManageBilling = isOrgOwner;
  const canViewAudit = isSuperAdmin || isOrgOwner;
  const isSoloOrg = !isSuperAdmin && (org?.member_count ?? 0) <= 1;
  // serverNow() instead of new Date(): the gate must follow the server's clock,
  // not the laptop's — a machine with a fast clock must not get locked out early.
  const isTrialExpired = !isSuperAdmin && !!org?.trial_ends_at && new Date(org.trial_ends_at).getTime() < serverNow();

  return (
    <AuthContext.Provider value={{
      user, session, profile, roles, org, loading, slowLoad,
      isSuperAdmin, isPlatformAdmin, canAccessAdminPanel,
      isOrgOwner, isOrgAdmin, isAgent,
      canManageTeam, canManageBilling, canViewAudit, isSoloOrg, isTrialExpired,
      isPlatformStaffOrg,
      isAdmin: isSuperAdmin,
      refreshUserData,
      signOut,
      signOutEverywhere,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');

  // Impersonation: override profile/org/roles for display purposes.
  // The real session is unchanged — all Supabase calls still use the admin's JWT.
  const impCtx = useContext(ImpersonationContext);
  if (impCtx?.isImpersonating && impCtx.impersonatedProfile) {
    const r = impCtx.impersonatedRoles;
    const isSuperAdmin = r.includes('super_admin');
    const isPlatformAdmin = r.includes('platform_admin');
    const canAccessAdminPanel = isSuperAdmin || isPlatformAdmin;
    const isOrgOwner  = r.includes('org_owner');
    const isOrgAdmin  = r.includes('org_admin');
    const isAgent     = r.includes('agent');
    return {
      ...ctx,
      profile:  impCtx.impersonatedProfile,
      org:      impCtx.impersonatedOrg,
      roles:    r,
      isSuperAdmin, isPlatformAdmin, canAccessAdminPanel,
      isOrgOwner, isOrgAdmin, isAgent,
      canManageTeam:    isSuperAdmin || isOrgOwner || isOrgAdmin,
      canManageBilling: isOrgOwner,
      canViewAudit:     isSuperAdmin || isOrgOwner,
      isSoloOrg:  !isSuperAdmin && (impCtx.impersonatedOrg?.member_count ?? 0) <= 1,
      isTrialExpired: !isSuperAdmin && !!impCtx.impersonatedOrg?.trial_ends_at && new Date(impCtx.impersonatedOrg.trial_ends_at) < new Date(),
      // Per-org gate — must be THEIR org's answer, never the admin's own.
      isPlatformStaffOrg: impCtx.impersonatedIsPlatformStaffOrg,
      isAdmin: isSuperAdmin,
    };
  }

  return ctx;
}
